import React, { FC, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Tabs, BoxV2 as Box, Accordion, Badge } from 'portal-commons';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons';
import queryString from 'query-string';

import { brandController, BrandDetail, BrandTrademark } from '@/apis/core';
import DetailsTab from './DetailsTab';
import Auth2faTab from './Auth2faTab';
import TrademarkTab from './TrademarkTab';
import SenderIdTab from './SenderIdTab';
import { Loader } from '@/shared_elements';
import { humanize } from '@/utils/string';
import { PageContainer } from '@/components';
import { InfoRow, TabPanel } from './components';
import { convertTimeWithTimezone } from '@/utils/time';
import { getVerticalTypes } from '@/shared_elements/actions/enumActionCreators';
import { STATUS_BADGE_COLOR_MAP } from '@/constants/enum-mapping';
import { ROUTES } from '@/constants/paths';

const tabs = [
  { label: 'Brand Details', id: 'details' },
  { label: 'Auth+ 2FA', id: 'authplus2fa' },
  { label: 'Trademark Verification', id: 'trademarkVerification' },
  { label: 'Sender ID', id: 'senderId' },
];

type TabName = 'details' | 'authplus2fa' | 'trademarkVerification' | 'senderId';

const BrandDetailDE: FC<{ brandUid: string | null }> = ({ brandUid }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [trademarks, setTrademarks] = useState<BrandTrademark[]>([]);
  const isDrawer =
    location.pathname === ROUTES.BRANDS || location.pathname === ROUTES.EVENTS;
  const verticalTypesData = useSelector(
    (state: any) => state.shareReducer.enums?.verticalTypes
  );

  const queryParams = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );

  const [activeTab, setActiveTab] = useState<TabName>(() => {
    if (!isDrawer && queryParams.tab) {
      const tab = queryParams.tab as string;
      if (tabs.some((t) => t.id === tab)) {
        return tab as TabName;
      }
    }
    return 'details';
  });

  const [brand, setBrand] = useState<BrandDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    dispatch(getVerticalTypes());
  }, [dispatch]);

  useEffect(() => {
    if (!isDrawer && queryParams.tab && queryParams.tab !== activeTab) {
      const tab = queryParams.tab as string;
      if (tabs.some((t) => t.id === tab)) {
        setActiveTab(tab as TabName);
      }
    }
  }, [queryParams.tab, isDrawer]);

  const fetchBrand = async (uid: string) => {
    setLoading(true);
    const data = await brandController.getBrand(uid);
    if (data) {
      setBrand(data);
      setLoading(false);
    }
  };

  const renderStatus = ({
    status,
    ...restProps
  }: {
    status: string | null | undefined;
    title: string;
    'data-testid': string;
  }) => {
    const baseBadgeProps: any = {
      size: 'lg',
      shape: 'rectangle',
      variant: 'fill',
      ...restProps,
    };
    if (!status) return 'N/A';
    const type = STATUS_BADGE_COLOR_MAP[status] || 'neutral';
    return (
      <Badge type={type} {...baseBadgeProps}>
        {humanize(status)}
      </Badge>
    );
  };

  useEffect(() => {
    if (brandUid) {
      fetchBrand(brandUid);
    }
    return () => {
      setBrand(null);
      setLoading(true);
      setActiveTab('details');
      setTrademarks([]);
    };
  }, [brandUid]);

  const handleTabChange = (tab: string) => {
    const tabName = tab as TabName;
    setActiveTab(tabName);

    if (!isDrawer) {
      const newSearch = queryString.stringify({
        ...queryParams,
        tab: tabName,
      });
      history.push({
        pathname: location.pathname,
        search: newSearch,
      });
    }
  };

  if (loading) return <Loader />;

  if (!brand) return null;

  return (
    <PageContainer
      sx={{
        lineHeight: 'normal',
        py: '24px',
        px: isDrawer ? '32px' : '96px',
      }}
    >
      <Box as="section" sx={{ width: '100%', mb: '24px' }}>
        <Box sx={{ fontSize: '24px', color: '#19262a', mb: '8px' }}>
          Brand Legal Name:{' '}
          <Box
            as="span"
            sx={{ fontWeight: 'bold' }}
            data-testid="brandLegalName"
          >
            {brand.companyName}
          </Box>
        </Box>
        <Box sx={{ fontSize: '14px', color: '#4d5659', mb: '8px' }}>
          Brand DBA Name:{' '}
          <Box as="span" sx={{ fontWeight: '600' }} data-testid="brandDbaName">
            {brand.displayName}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          {renderStatus({
            status: brand.identityStatus ?? 'PENDING',
            title: 'Identity Status',
            'data-testid': 'brandIdentityStatus',
          })}
          {renderStatus({
            status: brand.loaStatus,
            title: 'LOA Status',
            'data-testid': 'brandLoaStatus',
          })}
        </Box>
      </Box>

      <Accordion
        title="Company Overview"
        icon={faBuilding}
        sx={{ width: '100%', mb: '16px' }}
        defaultExpanded
        data-testid="brandDetailCompanyOverview"
      >
        <InfoRow col={3} sx={{ mb: '24px' }}>
          <InfoRow.Item label="Brand ID" data-testid="uid">
            {brand.uid}
          </InfoRow.Item>
          <InfoRow.Item label="Registered" data-testid="createDate">
            {convertTimeWithTimezone(brand.createDate, undefined, 'MM/DD/YYYY')}
          </InfoRow.Item>

          <InfoRow.Item
            label="Entity Type
            "
            data-testid="entityType"
          >
            {humanize(brand.entityType)}
          </InfoRow.Item>
        </InfoRow>
        <InfoRow col={3}>
          <InfoRow.Item
            label="Company registration number / ID"
            data-testid="ein"
          >
            {brand.ein}
          </InfoRow.Item>
        </InfoRow>
      </Accordion>

      <Box
        sx={{
          border: '1px solid #cccfd0',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          paddingX: '16px',
          paddingY: '24px',
          position: 'relative',
          width: '100%',
        }}
      >
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          sx={{ mb: '24px' }}
          data-testid="brandDetailTabs"
        />
        <TabPanel active={activeTab === 'details'}>
          <DetailsTab brand={brand} verticalTypes={verticalTypesData} />
        </TabPanel>
        <TabPanel active={activeTab === 'authplus2fa'}>
          <Auth2faTab brandUid={brandUid as string} />
        </TabPanel>
        <TabPanel active={activeTab === 'trademarkVerification'}>
          <TrademarkTab brand={brand} onDataChange={setTrademarks} />
        </TabPanel>
        <TabPanel active={activeTab === 'senderId'}>
          <SenderIdTab brand={brand} trademarks={trademarks} />
        </TabPanel>
      </Box>
    </PageContainer>
  );
};

export default BrandDetailDE;
